import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { User, Camera, Mail, Phone, Star, Award, Edit3, Trash2 } from 'lucide-react';
import { clearAuthSession } from '../../utils/authSession';
import { useNavigate } from 'react-router-dom';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';

interface Props {
  user: any;
}

const SUPABASE_URL = 'https://asicocgovrwxkwzrnfsx.supabase.co';
const SUPABASE_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFzaWNvY2dvdnJ3eGt3enJuZnN4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzg0MDYyNzAsImV4cCI6MjA5Mzk4MjI3MH0.19l0XZGt2ZzohJX3TwsH0JRy0dKr7LWxLzY32TkJ3aY';

const Profile: React.FC<Props> = ({ user }) => {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState(user.fullName || '');
  const [email, setEmail] = useState(user.email || '');
  const [avatarPreview, setAvatarPreview] = useState(user.avatarUrl || localStorage.getItem('avatar') || '');
  const [uploading, setUploading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // The driver app shows live trips/points/rating on its dashboard; this screen
  // was reading `user`, which is the localStorage snapshot written at login - so
  // a rider's trips and rating sat at whatever they were when they signed in
  // (0 and 5.0 for most people) and never moved, and points weren't shown at all.
  // Fetched fresh here, falling back to the stored values until it lands.
  const [stats, setStats] = useState<{ totalTrips: number; rating: number; points: number } | null>(null);
  const [offerPct, setOfferPct] = useState(0);
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;
    const auth = { Authorization: `Bearer ${token}` };
    fetch(`${API_URL}/auth/profile`, { headers: auth })
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => {
        if (!p) return;
        setStats({
          totalTrips: Number(p.totalTrips) || 0,
          rating: Number(p.rating) || 5,
          points: Number(p.points) || 0,
        });
        // Keep the stored user in step, so the rest of the app (and the next
        // launch) reads the same figures instead of the login-time snapshot.
        try {
          const stored = JSON.parse(localStorage.getItem('user') || '{}');
          localStorage.setItem('user', JSON.stringify({
            ...stored, totalTrips: p.totalTrips, rating: p.rating, points: p.points,
          }));
        } catch {}
      })
      .catch(() => {});
    // The rider's live loyalty discount - the same tiered percent the booking
    // flow applies, which until now they only ever saw as a badge mid-booking.
    fetch(`${API_URL}/pricing/my-offer`, { headers: auth })
      .then((r) => (r.ok ? r.json() : null))
      .then((o) => { if (o?.active) setOfferPct(Number(o.percent) || 0); })
      .catch(() => {});
  }, []);

  const totalTrips = stats?.totalTrips ?? (user.totalTrips || 0);
  const rating = stats?.rating ?? (user.rating || 5.0);
  const points = stats?.points ?? (user.points || 0);

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      const token = localStorage.getItem('accessToken');
      await fetch(`${API_URL}/auth/account`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) {}
    clearAuthSession();
    navigate('/');
  };

  const uploadAvatar = async (file: File) => {
    setUploading(true);
    try {
      const fileName = `user_${user.id}_${Date.now()}.jpg`;
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${SUPABASE_URL}/storage/v1/object/avatars/${fileName}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${SUPABASE_KEY}`,
        },
        body: file,
      });

      if (res.ok) {
        const avatarUrl = `${SUPABASE_URL}/storage/v1/object/public/avatars/${fileName}`;
        setAvatarPreview(avatarUrl);
        localStorage.setItem('avatar', avatarUrl);
        
        // Update user object
        const updatedUser = { ...user, avatarUrl };
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    } catch (err) {
      console.error('Upload failed:', err);
    }
    setUploading(false);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="px-4 pt-4 pb-24">
      <div className="flex flex-col items-center mb-6">
        <div className="relative">
          <div className="w-24 h-24 rounded-full bg-safaricab-gold/20 flex items-center justify-center overflow-hidden border-2 border-safaricab-gold/50">
            {avatarPreview ? (
              <img src={avatarPreview} alt="Profile" className="w-full h-full object-cover" />
            ) : (
              <User className="w-12 h-12 text-safaricab-gold" />
            )}
          </div>
          <label className="absolute bottom-0 right-0 w-8 h-8 bg-safaricab-gold rounded-full flex items-center justify-center cursor-pointer">
            {uploading ? (
              <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
            ) : (
              <Camera className="w-4 h-4 text-black" />
            )}
            <input 
              type="file" 
              accept="image/*" 
              className="hidden" 
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) uploadAvatar(file);
              }} 
            />
          </label>
        </div>
        <h2 className="text-white font-bold text-lg mt-3">{user.fullName}</h2>
        <div className="flex items-center gap-1 text-safaricab-gold text-sm mt-1">
          <Star className="w-4 h-4 fill-current" />
          <span>{Number(rating).toFixed(1)}</span>
        </div>
      </div>

      {/* Trips / rating / points - the same three figures the driver app puts on
          its dashboard, now live rather than the login-time snapshot. */}
      <div className="flex gap-3 mb-3">
        <div className="flex-1 bg-safaricab-card rounded-xl p-4 text-center border border-white/10">
          <p className="text-2xl text-white font-bold">{totalTrips}</p>
          <p className="text-white/50 text-xs mt-1">Total Trips</p>
        </div>
        <div className="flex-1 bg-safaricab-card rounded-xl p-4 text-center border border-white/10">
          <p className="text-2xl text-white font-bold">{Number(rating).toFixed(1)}</p>
          <p className="text-white/50 text-xs mt-1">Rating</p>
        </div>
        <div className="flex-1 bg-safaricab-card rounded-xl p-4 text-center border border-safaricab-gold/30">
          <p className="text-2xl text-safaricab-gold font-bold">{points}</p>
          <p className="text-white/50 text-xs mt-1">Points</p>
        </div>
      </div>

      {/* The rider's live loyalty discount (the tiered percent the booking flow
          already applies) - shown here so it's something they can look up, not
          just a badge that flashes past mid-booking. */}
      {offerPct > 0 && (
        <div className="flex items-center gap-3 bg-safaricab-gold/10 border border-safaricab-gold/30 rounded-xl p-4 mb-6">
          <Award className="w-5 h-5 text-safaricab-gold flex-shrink-0" />
          <div>
            <p className="text-safaricab-gold font-bold text-sm">{offerPct}% off your next ride</p>
            <p className="text-white/50 text-xs">Applied automatically at booking.</p>
          </div>
        </div>
      )}
      {offerPct === 0 && <div className="mb-6" />}

      <div className="space-y-4">
        <div className="space-y-2">
          <label className="text-white/60 text-xs font-medium uppercase">Full Name</label>
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} className="w-full bg-safaricab-card border border-white/10 rounded-xl py-3.5 pl-12 pr-4 text-white outline-none focus:border-safaricab-gold/50" />
          </div>
        </div>
        <div className="space-y-2">
          <label className="text-white/60 text-xs font-medium uppercase">Phone</label>
          <div className="relative">
            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <input type="tel" value={user.phone} disabled className="w-full bg-safaricab-card border border-white/10 rounded-xl py-3.5 pl-12 pr-4 text-white/50 outline-none" />
          </div>
        </div>
        <div className="space-y-2">
          <label className="text-white/60 text-xs font-medium uppercase">Email</label>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Add email" className="w-full bg-safaricab-card border border-white/10 rounded-xl py-3.5 pl-12 pr-4 text-white outline-none focus:border-safaricab-gold/50" />
          </div>
        </div>
        <motion.button whileTap={{ scale: 0.98 }} className="w-full bg-safaricab-gold text-black font-bold py-4 rounded-xl flex items-center justify-center gap-2">
          <Edit3 className="w-4 h-4" />
          Update Profile
        </motion.button>
        <button onClick={() => setShowDeleteConfirm(true)} className="w-full text-red-400/60 text-sm py-3 border border-red-500/20 rounded-xl flex items-center justify-center gap-2 mt-2">
          <Trash2 className="w-4 h-4" /> Delete Account
        </button>
      </div>

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/80 z-[300] flex items-center justify-center p-4">
          <div className="bg-safaricab-dark rounded-3xl p-6 w-full max-w-sm text-center">
            <Trash2 className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <h3 className="text-white font-bold text-lg mb-2">Delete Account?</h3>
            <p className="text-white/50 text-sm mb-6">This will permanently remove your account and all your data. This cannot be undone.</p>
            <button onClick={handleDeleteAccount} disabled={deleting} className="w-full bg-red-500 disabled:bg-red-500/40 text-white font-bold py-3 rounded-2xl mb-2">
              {deleting ? 'Deleting...' : 'Yes, Delete My Account'}
            </button>
            <button onClick={() => setShowDeleteConfirm(false)} className="w-full text-white/50 py-2 text-sm">Cancel</button>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default Profile;
